import { onBeforeUnmount, type Ref, ref, shallowRef, watch } from "vue";
import { LatLng, latLng, LatLngBounds, map, Map, Marker, Popup, tileLayer } from "leaflet";

import { CancelButton, ConfirmButton, LoadingState } from "~/client/components/controls/controls";
import { coords, courierData, getPickupPoints } from "~/client/elements/repository";
import { getElement } from "~/services/dom";
import { Sidebar } from "~/client/components/controls/sidebar";
import { dialog } from "~/client/components/modal/modal";
import { debounce } from "~/services/debounce";
import { form } from "~/client/elements/address";

import type { Location } from "~/client/types";

const DIALOG_CLASS = ".__innosend-dialog";
const LEAFLET_URL = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";

const DISTANCE_THRESHOLD_METERS_1000 = 1000;
const DISTANCE_THRESHOLD_METERS_2500 = 2500;

const SECONDS_FOR_ADDRESS_DEBOUNCE = 600;

export const [SelectCourierModal, openCourierModal, closeCourierModal, isCourierModalOpen] = dialog(() => import("./Maps.vue"));

export const leafletElement = shallowRef<Map | null>(null);
export const currentCenter = shallowRef<LatLng | null>(null);
export const currentLatLng = shallowRef<LatLng>();

export const currentLocation = ref<Location | null>(null);
export const currentIcon = ref<string>("");
export const currentServiceClass = ref<string>("");

export const currentOpenedPopup = shallowRef<(Marker<Popup> & { close: () => void }) | (Popup & { close: () => void }) | undefined>();

const debouncePickupPoints = debounce(getPickupPoints, SECONDS_FOR_ADDRESS_DEBOUNCE);

watch(form, debouncePickupPoints, { deep: true, immediate: true });
watch(coords, updateMapView, { deep: true });
watch(currentCenter, hanldeCurrentCenterChange);

function hanldeCurrentCenterChange(oldValue: LatLng | null, newValue: LatLng | null) {
	if (!oldValue || !newValue) return;

	if (oldValue.lat === newValue.lat && oldValue.lng === newValue.lng) {
		return;
	}

	form.value!.latitude = newValue.lat;
	form.value!.longitude = newValue.lng;
	debouncePickupPoints();
}

function updateMapView() {
	const currentCenter = leafletElement.value?.getCenter();
	const currentZoom = leafletElement.value?.getZoom();

	if (coords.value.lat !== currentCenter?.lat || coords.value.lng !== currentCenter?.lng) {
		leafletElement.value?.setView([coords.value.lat, coords.value.lng], currentZoom);
	}
}

function isLocationInBounds(locations?: Location[], bounds?: LatLngBounds): boolean {
	if (!locations) return false;

	return locations.some((location) => {
		const current = latLng(location.geo.latitude, location.geo.longitude);
		return bounds?.contains(current);
	});
}

const debounceCurrentLocation = debounce(updateCurrentLocation, 300);

function updateCurrentLocation(threshold: number) {
	const bounds = leafletElement.value?.getBounds();
	const center = leafletElement.value?.getCenter();
	const locations = courierData.value?.flatMap(({ locations }) => locations);

	if (!locations || !bounds || !center) return;

	const visibleLocations = locations.filter((location) => isLocationInBounds([location], bounds));

	if (visibleLocations.length === 0) {
		currentCenter.value = center;
		return;
	}

	const closestDistance = Math.min(
		...visibleLocations.map((location) => {
			const latLngs = latLng(location.geo.latitude, location.geo.longitude);
			return center.distanceTo(latLngs);
		})
	);

	if (closestDistance > threshold) {
		currentCenter.value = center;
	}
}

export function initMap(mapElement: Ref<HTMLElement | null>) {
	if (!mapElement.value) return;

	leafletElement.value = map(mapElement.value, {
		zoomControl: true,
		zoom: 15,
		zoomAnimation: true,
		fadeAnimation: true,
		markerZoomAnimation: true,
		attributionControl: false
	}).setView([coords.value.lat, coords.value.lng], 15);

	if (!leafletElement.value) return;

	tileLayer(LEAFLET_URL, {
		attribution: '&copy; <a href="https://carto.com/attributions">CARTO</a>',
		subdomains: "abcd",
		maxZoom: 19,
		tileSize: 256,
		detectRetina: true
	}).addTo(leafletElement.value);

	new Sidebar().addTo(leafletElement.value);
	new ConfirmButton().addTo(leafletElement.value);
	new CancelButton().addTo(leafletElement.value);
	new LoadingState().addTo(leafletElement.value);

	const dialog = getElement(DIALOG_CLASS, document);

	if (!dialog) {
		console.error("Dialog element not found.");
		return;
	}

	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			if (mutation.attributeName === "open") {
				leafletElement.value?.invalidateSize();
			}
		});
	});

	observer.observe(dialog!, { attributes: true });

	leafletElement.value?.on("popupclose", function () {
		currentOpenedPopup.value = undefined;
	});

	leafletElement.value?.on("popupopen", function (e) {
		const popup = e.popup;
		const contentElement = popup.getContent();

		if (contentElement instanceof HTMLElement) {
			const { id, icon, courier } = contentElement.dataset;

			currentLocation.value = JSON.parse(id!);
			currentIcon.value = icon ?? "";
			currentServiceClass.value = courier ?? "";

			if (currentLocation.value) {
				const element = document.getElementById(currentLocation.value.id);
				element?.scrollIntoView({ block: "center", behavior: "smooth" });

				if (window.setInnosendValues) {
					window.setInnosendValues(currentLocation.value.id ?? "", courier ?? "", JSON.stringify(currentLocation.value));
				}

				getElement("#innosend_courier_id", document)?.setAttribute("value", currentLocation.value.id ?? "");
				getElement("#innosend_courier_name", document)?.setAttribute("value", courier ?? "");
				getElement("#innosend_location_data", document)?.setAttribute("value", JSON.stringify(currentLocation.value));
			}
		} else {
			console.warn("Popup content is not an HTML element or is null.");
		}
	});

	leafletElement.value?.on("zoomend", function () {
		const zoomLevel = leafletElement.value!.getZoom();
		debounceCurrentLocation(zoomLevel > 15 ? DISTANCE_THRESHOLD_METERS_1000 : DISTANCE_THRESHOLD_METERS_2500);
	});

	leafletElement.value?.on("moveend", function () {
		const zoomLevel = leafletElement.value!.getZoom();
		debounceCurrentLocation(zoomLevel > 15 ? DISTANCE_THRESHOLD_METERS_1000 : DISTANCE_THRESHOLD_METERS_2500);
	});

	leafletElement.value?.on("popupopen", () => leafletElement.value?.invalidateSize());
	window.addEventListener("resize", () => leafletElement.value?.invalidateSize());

	onBeforeUnmount(() => {
		observer.disconnect();
		window.removeEventListener("resize", () => leafletElement.value?.invalidateSize());
	});
}
